import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import MessageFileInfoSchema from "messaging/attachments/_schemas/message-file-info-schema";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import ForumSchema from "messaging/forums/_schemas/forum-schema";
import Forum from "messaging/forums/forum";
import ReadReceiptSchema from "messaging/read-receipts/_schemas/read-receipt-schema";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import { z } from "zod";

export type MessageJSON = z.infer<typeof MessageSchema.MessageJSONZ>;
export type MessageParams = z.infer<typeof MessageSchema.MessageZ>;

export default class MessageSchema {
  public static readonly MessageJSONZ = z.object({
    id: z.uuid(),
    senderId: z.uuid().nullish(),
    forum: ForumSchema.ForumJSONZ,
    content: z.string().nullish(),
    subject: z.string().nullish(),
    created: z.iso.datetime({ offset: true }),
    /**
     * `deletedDate` is the name on the wire — `MessageResponse.DeletedDate`, camel-cased by the
     * default serializer. The client asked for `deleted`, which no response has ever carried, and
     * because the field is optional the mismatch parsed cleanly and reported every message as
     * un-deleted: a deletion round-tripped to a comment that reappeared with "<em>Deleted</em>" as
     * its body. `deleted` stays accepted so a locally persisted payload written by an older build
     * still decodes.
     */
    deletedDate: z.iso.datetime({ offset: true }).nullish(),
    deleted: z.iso.datetime({ offset: true }).nullish(),
    updated: z.iso.datetime({ offset: true }).nullish(),
    published: z.iso.datetime({ offset: true }).nullish(),
    /**
     * When the author last took a published message back. Absent on a message that has simply never
     * been published, which is the distinction it exists to draw — see `Message.retracted`.
     */
    retracted: z.iso.datetime({ offset: true }).nullish(),
    attachments: z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).nullish(),
    /**
     * How many attachments the message carries. Equal to `attachments.length` on a live message; on a
     * deleted one it is the only trace of them, because a tombstone indicates attachments without
     * presenting them — `MessageResponse.AttachmentCount` on the wire, and `attachments` is empty.
     */
    attachmentCount: z.number().nullish(),
    issues: z.array(z.string()).nullish(),
    readReceipts: z.array(ReadReceiptSchema.ReadReceiptJSONZ).nullish(),
    isAutoGenerated: z.boolean().nullish(),
  });

  public static readonly MessageZ = z.object({
    id: z.instanceof(Guid),
    senderId: z.instanceof(Guid).optional().nullable(),
    forum: z.instanceof(Forum),
    content: z.string().optional(),
    subject: z.string().optional(),
    attachments: z.array(z.instanceof(MessageFileInfo)).optional(),
    attachmentCount: z.number().optional(),
    readReceipts: z.array(z.instanceof(ReadReceipt)).optional(),
    issues: z.array(z.string()).optional(),
    isAutoGenerated: z.boolean().optional(),
    created: DateSchema.type.optional(),
    deleted: DateSchema.type.optional(),
    updated: DateSchema.type.optional(),
    published: DateSchema.type.optional(),
    retracted: DateSchema.type.optional(),
  });

  public static validate(values: unknown): MessageParams {
    return parseOrThrow(MessageSchema.MessageZ, values, {
      logError: true,
      schemaName: "MessageSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): MessageJSON {
    return parseOrThrow(MessageSchema.MessageJSONZ, json, {
      logError: true,
      schemaName: "MessageSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is MessageParams {
    try {
      MessageSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is MessageJSON {
    try {
      MessageSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): MessageParams {
    const parsed = parseOrThrow(MessageSchema.MessageJSONZ, json, {
      logError: true,
      schemaName: "MessageSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return MessageSchema.codec.decode(parsed);
  }

  public static serialize(params: MessageParams): MessageJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = MessageSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return MessageSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(MessageSchema.MessageJSONZ, MessageSchema.MessageZ, {
    decode: (data) => {
      const deletedAt = data.deletedDate ?? data.deleted;
      // A withheld message is a tombstone: the server hydrates it from a type that has no body, so it
      // arrives with neither content nor attachments by design. Requiring one of them here is what made
      // the "this comment was deleted" render unreachable from a real payload — and a retraction, which
      // sends its audience the same body-less shape, would have been rejected outright.
      if (!deletedAt && !data.retracted && !data.content) {
        if (!data.attachments || data.attachments.length === 0) {
          throw new Error("Message.Deserialize(): Content is required if there are no attachments");
        }
      }
      return {
        id: new Guid(data.id),
        senderId: Guid.fromJSON(data.senderId) ?? undefined,
        forum: Forum.fromJSON(data.forum),
        content: data.content ?? undefined,
        subject: data.subject ?? undefined,
        created: AHDate.fromJSON(data.created) ?? undefined,
        deleted: AHDate.fromJSON(data.deletedDate ?? data.deleted) ?? undefined,
        updated: AHDate.fromJSON(data.updated) ?? undefined,
        published: AHDate.fromJSON(data.published) ?? undefined,
        retracted: AHDate.fromJSON(data.retracted) ?? undefined,
        attachments: data.attachments?.map((attachment) => MessageFileInfo.fromJSON(attachment)) ?? [],
        attachmentCount: data.attachmentCount ?? data.attachments?.length ?? 0,
        issues: data.issues ?? [],
        readReceipts:
          data.readReceipts
            ?.map((receipt) => ReadReceipt.fromJSON(receipt))
            .filter((receipt): receipt is ReadReceipt => receipt !== null && receipt !== undefined) ?? [],
        isAutoGenerated: data.isAutoGenerated ?? false,
      };
    },
    encode: (message) => {
      if (!message.id) throw new Error("Message.Serialize(): Id is required");
      if (!message.created) throw new Error("Message.Serialize(): Created is required");
      return {
        id: message.id.toJSON(),
        senderId: message.senderId?.toJSON(),
        forum: message.forum.toJSON(),
        content: message.content,
        subject: message.subject,
        created: message.created.toJSON(),
        deletedDate: message.deleted?.toJSON(),
        updated: message.updated?.toJSON(),
        published: message.published?.toJSON(),
        retracted: message.retracted?.toJSON(),
        attachments: message.attachments?.map((attachment) => attachment.toJSON()) ?? [],
        attachmentCount: message.attachmentCount ?? message.attachments?.length ?? 0,
        issues: message.issues ?? [],
        readReceipts: message.readReceipts?.map((receipt) => receipt.toJSON()) ?? [],
        isAutoGenerated: message.isAutoGenerated ?? false,
      };
    },
  });
}
